import React from 'react';
import { Eye, Link, PenLine, Download, RefreshCw, TrendingUp } from 'lucide-react';
import { SettingsSection, SettingsToggle } from '../index';

const TabStockImport = ({ settings, onChange }) => {
  const updateSetting = (key, value) => {
    onChange({ ...settings, [key]: value });
  };

  return (
    <div className="space-y-6">
      {/* Overview of import methods */}
      <SettingsSection title="Sources de véhicules" icon={Download}>
        <p className="text-sm text-muted mb-4">
          Trois façons d'ajouter des véhicules à votre stock :
        </p>
        <div className="grid gap-3">
          {/* Method 1: Manual entry */}
          <div className="flex items-start gap-3 p-3 rounded-lg border border-dark-border bg-dark-hover">
            <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-full bg-orange-900/50 flex items-center justify-center">
              <PenLine className="h-4 w-4 text-orange-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-primary">Encodage manuel</p>
              <p className="text-xs text-muted">
                Ajoutez un véhicule manuellement en renseignant les informations et en uploadant les photos depuis la page Stock.
              </p>
            </div>
          </div>

          {/* Method 2: Manual link import */}
          <div className="flex items-start gap-3 p-3 rounded-lg border border-dark-border bg-dark-hover">
            <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-full bg-green-900/50 flex items-center justify-center">
              <Link className="h-4 w-4 text-green-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-primary">Import par lien</p>
              <p className="text-xs text-muted">
                Collez le lien d'une annonce AutoScout24 pour importer un véhicule spécifique depuis la page Stock.
              </p>
            </div>
          </div>

          {/* Method 3: AutoScout24 scraping */}
          <div className="flex items-start gap-3 p-3 rounded-lg border border-dark-border bg-dark-hover">
            <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-full bg-carmin-50 flex items-center justify-center">
              <Eye className="h-4 w-4 text-carmin" />
            </div>
            <div>
              <p className="text-sm font-medium text-primary">Surveillance automatique</p>
              <p className="text-xs text-muted">
                Vos annonces AutoScout24 sont importées automatiquement toutes les heures. Configurez l'URL ci-dessous.
              </p>
            </div>
          </div>
        </div>
      </SettingsSection>

      {/* AutoScout24 Monitoring Config */}
      <SettingsSection title="Surveillance AutoScout24" icon={Eye}>
        <div className="space-y-6">
          <SettingsToggle
            label="Surveillance de page AutoScout24"
            description="Importer automatiquement les nouvelles annonces (toutes les heures)"
            checked={settings?.dealer_monitoring_enabled || false}
            onChange={(value) => updateSetting('dealer_monitoring_enabled', value)}
          />

          {settings?.dealer_monitoring_enabled && (
            <div>
              <label className="block text-sm font-medium text-label mb-2">
                URL de votre page professionnelle AutoScout24
              </label>
              <input
                type="url"
                value={settings?.dealer_url || ''}
                onChange={(e) => updateSetting('dealer_url', e.target.value)}
                placeholder="https://www.autoscout24.be/vendeur/votre-garage"
                className="w-full bg-dark-input border border-dark-border text-primary placeholder-muted rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-carmin"
              />
              <p className="mt-2 text-sm text-muted">
                Formats acceptés : /professional/, /vendeur/, /dealer/, /concessionnaire/, /verkopers/
              </p>
            </div>
          )}

          {settings?.dealer_monitoring_enabled && settings?.dealer_url && (
            <>
              <div className="p-4 bg-carmin-50 border border-carmin/30 rounded-lg">
                <p className="text-sm text-carmin-light">
                  La surveillance est active. Les nouvelles annonces seront automatiquement importées et vous recevrez une notification.
                </p>
              </div>

              {/* Reappearing vehicle handling */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-label mb-1">
                  <RefreshCw className="h-4 w-4 text-blue-400" />
                  Véhicules qui réapparaissent
                </label>
                <p className="text-xs text-muted mb-3">
                  Quand un véhicule marqué comme vendu réapparaît sur AutoScout24 (rotation d'annonces par le marchand)
                </p>
                <div className="space-y-2">
                  <label
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      (settings?.reappearing_vehicle_action || 'manual_confirm') === 'manual_confirm'
                        ? 'border-carmin bg-carmin-50'
                        : 'border-dark-border bg-dark-hover hover:border-gray-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="reappearing_vehicle_action"
                      value="manual_confirm"
                      checked={(settings?.reappearing_vehicle_action || 'manual_confirm') === 'manual_confirm'}
                      onChange={() => updateSetting('reappearing_vehicle_action', 'manual_confirm')}
                      className="mt-0.5 accent-carmin"
                    />
                    <div>
                      <p className="text-sm font-medium text-primary">Confirmation manuelle</p>
                      <p className="text-xs text-muted">
                        Vous recevrez une notification pour décider de remettre le véhicule en stock ou non.
                      </p>
                    </div>
                  </label>
                  <label
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      settings?.reappearing_vehicle_action === 'auto_restore'
                        ? 'border-carmin bg-carmin-50'
                        : 'border-dark-border bg-dark-hover hover:border-gray-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="reappearing_vehicle_action"
                      value="auto_restore"
                      checked={settings?.reappearing_vehicle_action === 'auto_restore'}
                      onChange={() => updateSetting('reappearing_vehicle_action', 'auto_restore')}
                      className="mt-0.5 accent-carmin"
                    />
                    <div>
                      <p className="text-sm font-medium text-primary">Restauration automatique</p>
                      <p className="text-xs text-muted">
                        Le véhicule est automatiquement remis en stock. Vous recevrez une notification d'information.
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </>
          )}
        </div>
      </SettingsSection>

      {/* Opportunity Settings */}
      <SettingsSection title="Parametres Opportunites" icon={TrendingUp}>
        <div className="space-y-6">
          {/* Margin Table */}
          <div>
            <label className="block text-sm font-medium text-label mb-3">
              Marges brutes estimees par segment de prix
            </label>
            <p className="text-xs text-muted mb-3">
              Ces marges sont utilisees pour estimer le profit potentiel des opportunites quand le prix d'achat reel n'est pas renseigne.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(settings?.opportunity_margin_table || [
                { segment_min: 0, segment_max: 5000, margin_pct: 20 },
                { segment_min: 5000, segment_max: 15000, margin_pct: 14 },
                { segment_min: 15000, segment_max: 30000, margin_pct: 11 },
                { segment_min: 30000, segment_max: null, margin_pct: 8 },
              ]).map((seg, i) => (
                <div key={i} className="bg-dark-hover rounded-lg p-3 border border-dark-border">
                  <div className="text-xs text-muted mb-2">
                    {seg.segment_min.toLocaleString('fr-BE')} - {seg.segment_max ? `${seg.segment_max.toLocaleString('fr-BE')} EUR` : '+ EUR'}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number"
                      value={seg.margin_pct}
                      onChange={(e) => {
                        const table = [...(settings?.opportunity_margin_table || [
                          { segment_min: 0, segment_max: 5000, margin_pct: 20 },
                          { segment_min: 5000, segment_max: 15000, margin_pct: 14 },
                          { segment_min: 15000, segment_max: 30000, margin_pct: 11 },
                          { segment_min: 30000, segment_max: null, margin_pct: 8 },
                        ])];
                        table[i] = { ...table[i], margin_pct: parseFloat(e.target.value) || 0 };
                        updateSetting('opportunity_margin_table', table);
                      }}
                      className="w-16 bg-dark-input text-primary rounded px-2 py-1.5 text-sm border border-dark-border focus:outline-none focus:ring-1 focus:ring-carmin"
                      min="0" max="50"
                    />
                    <span className="text-muted text-sm">%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cost Defaults */}
          <div>
            <label className="block text-sm font-medium text-label mb-3">
              Couts par defaut par vehicule
            </label>
            <p className="text-xs text-muted mb-3">
              Valeurs par defaut appliquees au calcul de profit. Vous pouvez les personnaliser pour chaque vehicule sur sa fiche.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-muted mb-1">Preparation / reconditionnement (EUR)</label>
                <input
                  type="number"
                  value={settings?.opportunity_reconditioning ?? 0}
                  onChange={(e) => updateSetting('opportunity_reconditioning', parseFloat(e.target.value) || 0)}
                  className="w-full bg-dark-input text-primary rounded-md px-3 py-2 border border-dark-border focus:outline-none focus:ring-1 focus:ring-carmin"
                  min="0" placeholder="0"
                />
                <p className="text-xs text-muted mt-1">Nettoyage, polish, petite remise en etat</p>
              </div>
              <div>
                <label className="block text-xs text-muted mb-1">Frais fixes (EUR)</label>
                <input
                  type="number"
                  value={settings?.opportunity_fixed_costs ?? 0}
                  onChange={(e) => updateSetting('opportunity_fixed_costs', parseFloat(e.target.value) || 0)}
                  className="w-full bg-dark-input text-primary rounded-md px-3 py-2 border border-dark-border focus:outline-none focus:ring-1 focus:ring-carmin"
                  min="0" placeholder="0"
                />
                <p className="text-xs text-muted mt-1">Admin, paperasse, CT, immatriculation</p>
              </div>
              <div>
                <label className="block text-xs text-muted mb-1">Transport (EUR)</label>
                <input
                  type="number"
                  value={settings?.opportunity_transport ?? 0}
                  onChange={(e) => updateSetting('opportunity_transport', parseFloat(e.target.value) || 0)}
                  className="w-full bg-dark-input text-primary rounded-md px-3 py-2 border border-dark-border focus:outline-none focus:ring-1 focus:ring-carmin"
                  min="0" placeholder="0"
                />
                <p className="text-xs text-muted mt-1">Rapatriement moyen d'un vehicule</p>
              </div>
              <div>
                <label className="block text-xs text-muted mb-1">Garantie (EUR)</label>
                <input
                  type="number"
                  value={settings?.opportunity_guarantee ?? 0}
                  onChange={(e) => updateSetting('opportunity_guarantee', parseFloat(e.target.value) || 0)}
                  className="w-full bg-dark-input text-primary rounded-md px-3 py-2 border border-dark-border focus:outline-none focus:ring-1 focus:ring-carmin"
                  min="0" placeholder="0"
                />
                <p className="text-xs text-muted mt-1">Cout moyen de garantie par vehicule</p>
              </div>
              <div>
                <label className="block text-xs text-muted mb-1">Frais de publication (EUR)</label>
                <input
                  type="number"
                  value={settings?.opportunity_publication ?? 0}
                  onChange={(e) => updateSetting('opportunity_publication', parseFloat(e.target.value) || 0)}
                  className="w-full bg-dark-input text-primary rounded-md px-3 py-2 border border-dark-border focus:outline-none focus:ring-1 focus:ring-carmin"
                  min="0" placeholder="0"
                />
                <p className="text-xs text-muted mt-1">Photos pro, annonces payantes</p>
              </div>
              <div>
                <label className="block text-xs text-muted mb-1">Cout stockage/jour (EUR)</label>
                <input
                  type="number"
                  value={settings?.opportunity_daily_storage_cost ?? 0}
                  onChange={(e) => updateSetting('opportunity_daily_storage_cost', parseFloat(e.target.value) || 0)}
                  className="w-full bg-dark-input text-primary rounded-md px-3 py-2 border border-dark-border focus:outline-none focus:ring-1 focus:ring-carmin"
                  min="0" placeholder="0" step="0.5"
                />
                <p className="text-xs text-muted mt-1">Parking, espace couvert</p>
              </div>
            </div>
          </div>

          {/* Sort Preference */}
          <div>
            <label className="block text-sm font-medium text-label mb-2">
              Tri par defaut des opportunites
            </label>
            <select
              value={settings?.opportunity_sort_preference || 'profit'}
              onChange={(e) => updateSetting('opportunity_sort_preference', e.target.value)}
              className="w-full md:w-64 bg-dark-input text-primary rounded-md px-3 py-2 border border-dark-border focus:outline-none focus:ring-1 focus:ring-carmin"
            >
              <option value="profit">Profit potentiel</option>
              <option value="velocity">Revente rapide</option>
              <option value="motivation">Motivation vendeur</option>
            </select>
          </div>
        </div>
      </SettingsSection>
    </div>
  );
};

export default TabStockImport;
